import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useToast } from '../hooks/useToast'

interface StaffAssignment {
  id: string
  userId: string
  departmentId: string
  role: string
  expertiseLevel: number
  maxConcurrentTickets: number
  notes?: string
  isActive: boolean
  assignedAt: string
  user: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  department: {
    id: string
    name: string
    description?: string
  }
}

interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  isActive: boolean
}

interface Department {
  id: string
  name: string
  description?: string
}

const StaffAssignments: React.FC = () => {
  const { showSuccess, showError } = useToast()
  const [assignments, setAssignments] = useState<StaffAssignment[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState<StaffAssignment | null>(null)
  
  // Form state
  const [formData, setFormData] = useState({
    userId: '',
    departmentId: '',
    role: 'MEMBER',
    expertiseLevel: 1,
    maxConcurrentTickets: 10,
    notes: ''
  })

  const roles = [
    { value: 'LEAD', label: 'Team Lead', description: 'Primary responsible person' },
    { value: 'MEMBER', label: 'Team Member', description: 'Standard team member' },
    { value: 'BACKUP', label: 'Backup', description: 'Secondary support' },
    { value: 'SPECIALIST', label: 'Specialist', description: 'Expert in specific area' }
  ]

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [assignmentsRes, usersRes, departmentsRes] = await Promise.all([
        axios.get('/api/staff-assignments'),
        axios.get('/api/organizations/users'),
        axios.get('/api/tickets/departments')
      ])
      
      setAssignments(assignmentsRes.data)
      setUsers(usersRes.data.data) // Note: organizations/users returns {success: true, data: users}
      setDepartments(departmentsRes.data.data) // Note: tickets/departments returns {success: true, data: departments}
    } catch (error) {
      console.error('Error fetching data:', error)
      showError('Failed to fetch data')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (editingAssignment) {
        await axios.put(`/api/staff-assignments/${editingAssignment.id}`, formData)
        showSuccess('Assignment updated successfully')
      } else {
        await axios.post('/api/staff-assignments', formData)
        showSuccess('Assignment created successfully')
      }
      
      setShowCreateModal(false)
      setEditingAssignment(null)
      resetForm()
      fetchData()
    } catch (error: any) {
      console.error('Error saving assignment:', error)
      showError(error.response?.data?.error || 'Failed to save assignment')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this assignment?')) return
    
    try {
      await axios.delete(`/api/staff-assignments/${id}`)
      showSuccess('Assignment deleted successfully')
      fetchData()
    } catch (error: any) {
      console.error('Error deleting assignment:', error)
      showError(error.response?.data?.error || 'Failed to delete assignment')
    }
  }

  const handleEdit = (assignment: StaffAssignment) => {
    console.log('handleEdit called with:', assignment)
    console.log('Current users:', users)
    console.log('Current departments:', departments)
    
    setEditingAssignment(assignment)
    setFormData({
      userId: assignment.userId,
      departmentId: assignment.departmentId,
      role: assignment.role,
      expertiseLevel: assignment.expertiseLevel,
      maxConcurrentTickets: assignment.maxConcurrentTickets,
      notes: assignment.notes || ''
    })
    setShowCreateModal(true)
  }

  const resetForm = () => {
    setFormData({
      userId: '',
      departmentId: '',
      role: 'MEMBER',
      expertiseLevel: 1,
      maxConcurrentTickets: 10,
      notes: ''
    })
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'LEAD': return 'bg-blue-100 text-blue-800'
      case 'MEMBER': return 'bg-green-100 text-green-800'
      case 'BACKUP': return 'bg-yellow-100 text-yellow-800'
      case 'SPECIALIST': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getExpertiseColor = (level: number) => {
    if (level >= 4) return 'text-green-600'
    if (level >= 3) return 'text-blue-600'
    if (level >= 2) return 'text-yellow-600'
    return 'text-gray-600'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading staff assignments...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Staff Department Assignments</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage team assignments and workload distribution across departments
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => {
              setEditingAssignment(null)
              resetForm()
              setShowCreateModal(true)
            }}
            className="btn-primary"
          >
            Add Assignment
          </button>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="mt-8 card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staff Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expertise
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Max Tickets
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assignments.map((assignment) => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {assignment.user.firstName} {assignment.user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{assignment.user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {assignment.department.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(assignment.role)}`}>
                      {roles.find(r => r.value === assignment.role)?.label || assignment.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getExpertiseColor(assignment.expertiseLevel)}`}>
                      Level {assignment.expertiseLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {assignment.maxConcurrentTickets}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {assignment.assignedAt ? new Date(assignment.assignedAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(assignment)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(assignment.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingAssignment ? 'Edit Assignment' : 'Create Assignment'}
              </h3>
              
              {/* Debug info */}
              <div className="mb-4 p-2 bg-gray-100 text-xs">
                <p>Users: {users?.length || 0}</p>
                <p>Departments: {departments?.length || 0}</p>
                <p>Form Data: {JSON.stringify(formData)}</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Member
                  </label>
                  <select
                    value={formData.userId}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Select staff member</option>
                    {users.filter(u => u.isActive).map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={formData.departmentId}
                    onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Select department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="input-field"
                    required
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expertise Level (1-5)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={formData.expertiseLevel}
                    onChange={(e) => setFormData({ ...formData, expertiseLevel: parseInt(e.target.value) })}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Concurrent Tickets
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxConcurrentTickets}
                    onChange={(e) => setFormData({ ...formData, maxConcurrentTickets: parseInt(e.target.value) })}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input-field"
                    rows={3}
                    placeholder="Optional notes about this assignment..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false)
                      setEditingAssignment(null)
                      resetForm()
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    {editingAssignment ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StaffAssignments
